import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    // Cookie-based session: just ask the server who we are. The httpOnly
    // cookie travels automatically with `withCredentials: true`.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "use_school_portal") {
        return {
          ok: false,
          error: detail.message || "Please sign in through your school's portal.",
          usePortal: true,
          slug: detail.slug || null,
        };
      }
      if (detail && typeof detail === "object" && detail.code === "login_locked") {
        return { ok: false, error: detail.message || "Too many failed attempts. Please try again later.", locked: true };
      }
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const schoolPortalLogin = async (slug, email, password) => {
    try {
      const { data } = await api.post(`/public/schools/${slug}/login`, { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const parentLogin = async (slug, email, password) => {
    try {
      const { data } = await api.post(`/public/schools/${slug}/parent-login`, { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    // Cookie-only auth: ask the server to evict the httpOnly cookie. Failure
    // here doesn't block the local sign-out (e.g. offline / network errors).
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    // Legacy localStorage keys + selection prefs cleanup.
    localStorage.removeItem("sb_token");
    localStorage.removeItem("sb_selected_school");
    localStorage.removeItem("sb_selected_branch");
    setUser(null);
  };

  const updateUser = (patch) => setUser((u) => (u ? { ...u, ...patch } : u));

  return (
    <AuthContext.Provider value={{ user, checking, login, register, schoolPortalLogin, parentLogin, logout, refresh, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
